import { z } from 'zod';
import { uuid, isoTimestamp } from '../schemas/common.js';
/**
 * The `/huub` screen's API (docs/ASSISTENT.md §11). Server-owned state, read and
 * written online only — nothing here is in a phone's Dexie, like the recipe
 * settings screen.
 */
/** 'HH:MM', Europe/Amsterdam wall-clock time. */
export const briefingTime = z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/);
/** How many live memories Huub keeps before the screen says it is full (§8). */
export const ASSISTANT_MEMORY_CAP = 300;
/** The longest memory or character note, in characters (migration 050). */
export const ASSISTANT_MEMORY_MAX_LENGTH = 2000;
export const MEMORY_SOURCES = ['told', 'picked_up'];
/** A fact about the world, or a note on Huub's own character (§8.1). */
export const MEMORY_KINDS = ['fact', 'persona'];
/**
 * Is the Signal link up? Written by the worker that holds the socket; the api
 * only reads it. `off` = not configured at all; `stale` = configured, but the
 * worker has not checked in recently (it is down, or signal-cli is).
 */
export const ASSISTANT_LINK_STATES = ['off', 'connected', 'stale', 'never'];
export const assistantMember = z.object({
    user_id: uuid,
    display_name: z.string(),
    paired: z.boolean(),
    paired_at: isoTimestamp.nullable(),
});
export const assistantMemory = z.object({
    id: uuid,
    text: z.string(),
    source: z.enum(MEMORY_SOURCES),
    kind: z.enum(MEMORY_KINDS),
    about_name: z.string().nullable(),
    created_by_name: z.string().nullable(),
    created_at: isoTimestamp,
});
export const assistantSettings = z.object({
    always_respond: z.boolean(),
    briefing_enabled: z.boolean(),
    briefing_time: briefingTime,
    /** The evening-before bin reminder (§10.1). */
    afval_enabled: z.boolean(),
    /** The evening Het Woord nudge for whoever has not played yet (§10.2). */
    wordle_nudge_enabled: z.boolean(),
    wordle_nudge_time: briefingTime,
});
export const assistantStatusResponse = z.object({
    name: z.string(),
    /** Both Signal settings present — he can exist at all. */
    configured: z.boolean(),
    /** The Scaleway key is present — he can think, not only remind. */
    model_configured: z.boolean(),
    link: z.object({
        state: z.enum(ASSISTANT_LINK_STATES),
        heartbeat_at: isoTimestamp.nullable(),
        last_error: z.string().nullable(),
    }),
    /** A group has been bound by the first pairing. */
    group_bound: z.boolean(),
    settings: assistantSettings,
    members: z.array(assistantMember),
    memories: z.array(assistantMemory),
    memory_cap: z.number().int(),
});
export const assistantPairingResponse = z.object({
    code: z.string().regex(/^[0-9]{6}$/),
    expires_at: isoTimestamp,
});
export const assistantSettingsRequest = assistantSettings.partial().refine((v) => Object.keys(v).length > 0, { message: 'nothing to update' });
/** Edit a memory's text, or move it between the facts and his character (§8.1). */
export const assistantMemoryPatch = z
    .object({ kind: z.enum(MEMORY_KINDS), text: z.string().trim().min(1).max(ASSISTANT_MEMORY_MAX_LENGTH) })
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'nothing to update' });
/** Write one from the app, as if told in the group. */
export const assistantMemoryCreate = z.object({
    kind: z.enum(MEMORY_KINDS),
    text: z.string().trim().min(1).max(ASSISTANT_MEMORY_MAX_LENGTH),
});
//# sourceMappingURL=schemas.js.map